import { useState, useEffect } from 'react';
import './AnalyticsTab.css';
import { supabase } from './supabase';
import { calcAnalytics, loadAndMergeTradesFromSupabase } from './tradeUtils';
import { useEffectiveUser } from './ImpersonationContext';
import Badge from './components/Badge';
import LiveDot from './components/LiveDot';
import StatCard from './components/StatCard';

function timeAgo(ts) {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60)    return 'à l\'instant';
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

//  Lit les trades du bot depuis localStorage
const loadBotTrades = () => {
  try {
    const saved = localStorage.getItem('bot-trades');
    return saved ? JSON.parse(saved) : [];
  } catch { return []; }
};

//  Structure d'un trade bot :
// { id, symbol, mint, entreeTs, sortieTs, achatUsd, venteUsd, pnlUsd, roi, roiPos, resultat, walletSuivi, txHash }

export default function AnalyticsTab() {
  const [period, setPeriod] = useState('30D');
  const [sortBy, setSortBy] = useState('date');
  const [trades, setTrades] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [expandedWallet, setExpandedWallet] = useState(null);
  const [hypotheticalAmount, setHypotheticalAmount] = useState('');
  const [copiedAddr, setCopiedAddr] = useState(null);

  const { effectiveUserId, isImpersonating } = useEffectiveUser();

  useEffect(() => {
    const load = async () => {
      try {
        const merged = await loadAndMergeTradesFromSupabase(effectiveUserId, isImpersonating);
        if (merged) setTrades(merged);
        else setTrades(isImpersonating ? [] : loadBotTrades());
      } catch { setTrades(loadBotTrades()); }
    };
    load();
    const iv = setInterval(load, 30000);
    let sub = null;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) return;
      const targetId = effectiveUserId || session.user.id;
      sub = supabase.channel(`analytics-trades-${targetId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'trades',
          filter: `user_id=eq.${targetId}` }, load)
        .subscribe();
    });
    return () => { clearInterval(iv); sub?.unsubscribe(); };
  }, [effectiveUserId]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) return;
      const targetId = effectiveUserId || session.user.id;
      supabase.from('configs').select('name, emoji, target_wallet')
        .eq('user_id', targetId)
        .then(({ data }) => {
          if (data) setConfigs(data.map(c => ({ name: c.name, emoji: c.emoji, targetWallet: c.target_wallet })));
        });
    });
  }, [effectiveUserId]);

  // Filtrer par période (sortieTs en priorité sur entreeTs)
  const now = Date.now() / 1000;
  const periodeMap = { '7D': 7*86400, '30D': 30*86400, '90D': 90*86400, 'ALL': Infinity };
  const filtered = trades.filter(t => {
    const ts  = t.sortieTs || t.entreeTs || 0;
    const age = now - ts;
    return age <= (periodeMap[period] || Infinity);
  });

  // simTradeOverride — what-if: recalcule le PNL sim avec un montant personnalisé
  const simTradeOverride = parseFloat(localStorage.getItem('sim-trade-amount') || '0');
  const filteredDisplay  = simTradeOverride > 0
    ? filtered.map(t => t.isSim && t.achatUsd > 0
        ? { ...t, pnlUsd: t.pnlUsd != null ? t.pnlUsd * (simTradeOverride / t.achatUsd) : null, achatUsd: simTradeOverride }
        : t)
    : filtered;

  // Stats via calcAnalytics (exclut 'En cours' et 'Fermé (historique)')
  const stats        = calcAnalytics(filteredDisplay);
  const totalTrades  = stats?.total        ?? 0;
  const gagnants     = stats?.winners      ?? 0;
  const perdants     = stats?.losers       ?? 0;
  const pnlTotal     = stats?.totalPnl     ?? 0;
  const totalInvesti   = stats?.totalInvested ?? 0;
  const capitalEnCours = filtered.filter(t => t.resultat === 'En cours').reduce((s,t) => s + (t.achatUsd || 0), 0);
  const roi          = stats?.totalPnlPct  ?? 0;
  const winRate      = stats?.winRate      ?? 0;
  const gainMoy      = stats?.gainMoy      ?? 0;
  const perteMoy     = stats?.perteMoy     ?? 0;

  const fmtUsd = (n) => {
    const sign = n >= 0 ? (n > 0 ? '+': '') : '-';
    const abs = Math.abs(n);
    if (abs >= 1e6) return `${sign}$${(abs/1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}$${(abs/1e3).toFixed(1)}K`;
    return `${sign}$${abs.toFixed(0)}`;
  };

  // Trier les trades
  const sorted = [...filteredDisplay].sort((a, b) => {
    if (sortBy === 'date')    return (b.entreeTs||0) - (a.entreeTs||0);
    if (sortBy === 'pnl')     return (b.pnlUsd||0) - (a.pnlUsd||0);
    if (sortBy === 'roi')     return (b.roi||0) - (a.roi||0);
    return 0;
  });

  // PNL cumulatif pour le graphique
  const pnlCurve = (() => {
    let cumul = 0;
    return [...filteredDisplay].sort((a,b) => (a.sortieTs||a.entreeTs||0)-(b.sortieTs||b.entreeTs||0)).map(t => {
      cumul += t.pnlUsd || 0;
      const ts = t.sortieTs || t.entreeTs;
      return { cumul, date: ts ? new Date(ts*1000).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit'}) : ''};
    });
  })();

  // Export CSV
  const exportCSV = () => {
    const headers = ['Date','Token','Wallet Copié','Investi ($)','Vente ($)','PNL ($)','ROI (%)','Résultat'];
    const rows = sorted.map(t => [
      t.entreeTs ? new Date(t.entreeTs*1000).toLocaleDateString('fr-FR') : '',
      t.symbol || '',
      t.walletSuivi || '',
      (t.achatUsd || 0).toFixed(2),
      (t.venteUsd || 0).toFixed(2),
      (t.pnlUsd || 0).toFixed(2),
      t.roi !== undefined ? t.roi.toFixed(1) : '',
      t.resultat || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trades_${new Date().toLocaleDateString('fr-FR').replace(/\//g,'-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Wallets les plus copiés
  const walletStats = {};
  filteredDisplay.forEach(t => {
    if (!t.walletSuivi) return;
    if (!walletStats[t.walletSuivi]) walletStats[t.walletSuivi] = { trades:0, pnl:0, wins:0, bestPnl:-Infinity, worstPnl:Infinity, invested:0, roiList:[] };
    walletStats[t.walletSuivi].trades++;
    walletStats[t.walletSuivi].pnl += t.pnlUsd || 0;
    walletStats[t.walletSuivi].invested += t.achatUsd || 0;
    if (t.resultat === 'Gagnant') walletStats[t.walletSuivi].wins++;
    const p = t.pnlUsd || 0;
    if (p > walletStats[t.walletSuivi].bestPnl) walletStats[t.walletSuivi].bestPnl = p;
    if (p < walletStats[t.walletSuivi].worstPnl) walletStats[t.walletSuivi].worstPnl = p;
    walletStats[t.walletSuivi].roiList.push(t.roi || 0);
  });
  const topWallets = Object.entries(walletStats).sort((a,b) => b[1].pnl - a[1].pnl);

  return (
    <div className="an-root">

      {/* Period filter */}
      <div className="an-period-bar">
        <div className="an-periods">
          {['7D','30D','90D','ALL'].map(p=>(
            <button key={p} className={`an-period ${period===p?'active':''}`} onClick={()=>setPeriod(p)}>{p}</button>
          ))}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <LiveDot color={trades.length>0?'accent':'inactive'} size={8} pulse={trades.length>0} />
          <span style={{fontSize:11,color:'#5a6a80'}}>{trades.length > 0 ? `${trades.length} trade(s) bot`: 'En attente du bot'}</span>
        </div>
      </div>

      {/* Stats cards */}
      <div className="an-stat-cards">
        <StatCard
          label="TRADES EXÉCUTÉS"
          value={totalTrades}
          sub={`${gagnants} gagnants · ${perdants} perdants`}
          trend="neutral"
          icon={<svg viewBox="0 0 20 20"fill="none"width="16"height="16"><path d="M3 10h14M10 3l7 7-7 7"stroke="#00e5cc"strokeWidth="1.4"strokeLinecap="round"strokeLinejoin="round"/></svg>}
        />
        <StatCard
          label="PNL TOTAL BOT"
          value={totalTrades>0?fmtUsd(pnlTotal):'—'}
          sub={`En cours: ${capitalEnCours>0?fmtUsd(capitalEnCours):'—'}`}
          trend={totalTrades>0?(pnlTotal>=0?'accent':'down'):'neutral'}
          icon={<svg viewBox="0 0 20 20"fill="none"width="16"height="16"><path d="M4 10l4 4 8-8"stroke="#00e5cc"strokeWidth="1.4"strokeLinecap="round"strokeLinejoin="round"/></svg>}
          iconColor="success"
        />
        <StatCard
          label="ROI BOT"
          value={totalTrades>0?`${roi>=0?'+':''}${roi.toFixed(1)}%`:'—'}
          sub="Sur capital investi"
          trend={totalTrades>0?(roi>=0?'accent':'down'):'neutral'}
          icon={<svg viewBox="0 0 20 20"fill="none"width="16"height="16"><rect x="2"y="10"width="3"height="8"rx=".5"fill="#00e5cc"/><rect x="7"y="6"width="3"height="12"rx=".5"fill="#00e5cc"opacity=".7"/><rect x="12"y="3"width="3"height="15"rx=".5"fill="#00e5cc"opacity=".5"/></svg>}
          iconColor="pnl"
        />
        <StatCard
          label="WIN RATE BOT"
          value={totalTrades>0?`${winRate.toFixed(1)}%`:'—'}
          sub={totalTrades>0?`Gain moy: ${fmtUsd(gainMoy)}`:'Aucun trade'}
          trend="warning"
          icon={<svg viewBox="0 0 20 20"fill="none"width="16"height="16"><rect x="2"y="4"width="16"height="12"rx="2"stroke="#f0c040"strokeWidth="1.4"/><rect x="6"y="8"width="8"height="4"rx="1"fill="#f0c040"opacity=".5"/></svg>}
          iconColor="warning"
        />
      </div>

      {/* Etat vide */}
      {totalTrades === 0 && (
        <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'60px 20px',textAlign:'center',marginBottom:16}}>
          <div style={{fontSize:48,marginBottom:16}}></div>
          <div style={{fontSize:18,fontWeight:700,color:'#e0e4ec',marginBottom:8}}>En attente des trades du bot</div>
          <div style={{fontSize:13,color:'#5a6a80',maxWidth:400,margin:'0 auto',lineHeight:1.7}}>
            Une fois le bot de trading configuré et actif, ses performances apparaîtront ici en temps réel.
          </div>
          <div style={{marginTop:20,display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap'}}>
            {[['','Trades automatiques','Le bot copie les wallets suivis'],['','Analytics en temps réel','PNL, ROI, Win Rate mis à jour'],['','Alertes Telegram','Notification à chaque trade']].map(([icon,title,desc])=>(
              <div key={title} style={{background:'#0a0d14',border:'1px solid #1a2030',borderRadius:8,padding:'16px 20px',maxWidth:200,textAlign:'center'}}>
                <div style={{fontSize:24,marginBottom:8}}>{icon}</div>
                <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:4}}>{title}</div>
                <div style={{fontSize:11,color:'#5a6a80'}}>{desc}</div>
              </div>
            ))}
          </div>

        </div>
      )}

      {/* Graphique PNL cumulatif */}
      {totalTrades > 0 && pnlCurve.length > 1 && (
        <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px',marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:12,display:'flex',alignItems:'center',gap:7}}>
            <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><path d="M2 10l3-4 3 2 4-6"stroke="#00e5cc"strokeWidth="1.3"strokeLinecap="round"strokeLinejoin="round"/></svg>
            Évolution du PNL Bot
          </div>
          {(() => {
            const W=560,H=100,PAD=10;
            const vals=pnlCurve.map(p=>p.cumul);
            const minV=Math.min(...vals,0),maxV=Math.max(...vals,0);
            const range=maxV-minV||1;
            const x=(i)=>PAD+(i/(pnlCurve.length-1))*(W-PAD*2);
            const y=(v)=>PAD+(1-(v-minV)/range)*(H-PAD*2);
            const path=pnlCurve.map((p,i)=>`${i===0?'M':'L'}${x(i).toFixed(1)},${y(p.cumul).toFixed(1)}`).join('');
            const fill=path+`L${x(pnlCurve.length-1).toFixed(1)},${H-PAD} L${PAD},${H-PAD} Z`;
            const last=vals[vals.length-1];
            const col=last>=0?'#00e5cc':'#ff6b6b';
            const zeroY=y(0);
            return (
              <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:100}}>
                {zeroY>PAD&&zeroY<H-PAD&&<line x1={PAD} y1={zeroY} x2={W-PAD} y2={zeroY} stroke="#1a2030"strokeWidth="1"strokeDasharray="4,3"/>}
                <path d={fill} fill={last>=0?'rgba(0,229,204,0.07)':'rgba(255,107,107,0.07)'}/>
                <path d={path} fill="none"stroke={col} strokeWidth="1.5"strokeLinecap="round"strokeLinejoin="round"/>
                <circle cx={x(pnlCurve.length-1)} cy={y(last)} r="3"fill={col}/>
              </svg>
            );
          })()}
          <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'#3a4a5a',marginTop:4}}>
            <span>{pnlCurve[0]?.date}</span>
            <span style={{color:pnlTotal>=0?'#00e5cc':'#ff6b6b',fontWeight:700}}>PNL cumulé: {fmtUsd(pnlTotal)}</span>
            <span>{pnlCurve[pnlCurve.length-1]?.date}</span>
          </div>
        </div>
      )}

      {/* Stats détaillées + Top wallets */}
      {totalTrades > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>

          {/* Stats détaillées */}
          <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px'}}>
            <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:14,display:'flex',alignItems:'center',gap:7}}>
              <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><rect x="1"y="1"width="12"height="12"rx="2"stroke="#00e5cc"strokeWidth="1.2"/><path d="M4 7h6M4 4.5h4M4 9.5h3"stroke="#00e5cc"strokeWidth="1.2"strokeLinecap="round"/></svg>
              Résumé Bot
            </div>
            {[
              ['Trades gagnants', `${gagnants} (${winRate.toFixed(1)}%)`, gagnants > 0 ? 'pos': ''],
              ['Trades perdants', `${perdants}`, perdants > 0 ? 'neg': ''],
              ['Gain moyen', gainMoy > 0 ? fmtUsd(gainMoy) : '—', 'pos'],
              ['Perte moyenne', perteMoy > 0 ? fmtUsd(perteMoy) : '—', 'neg'],
              ['Capital en cours', capitalEnCours > 0 ? fmtUsd(capitalEnCours) : '—', ''],
              ['PNL net', fmtUsd(pnlTotal), pnlTotal >= 0 ? 'pos': 'neg'],
            ].map(([label, val, cls]) => (
              <div key={label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #0d1117',fontSize:12}}>
                <span style={{color:'#5a6a80'}}>{label}</span>
                <span style={{fontWeight:700,color:cls==='pos'?'#00e5cc':cls==='neg'?'#ff6b6b':'#c0c8d8'}}>{val}</span>
              </div>
            ))}
          </div>

          {/* Stats par wallet copié */}
          <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px'}}>
            <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:14,display:'flex',alignItems:'center',gap:7}}>
              <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><path d="M7 1l1.5 3.5H12l-2.8 2 1.1 3.5L7 8l-3.3 2 1.1-3.5L2 4.5h3.5z"fill="#f0c040"opacity=".8"/></svg>
              Stats par Wallet Copié
            </div>
            {/* Input hypothétique */}
            <div style={{marginBottom:12,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <div style={{display:'flex',alignItems:'center',gap:6,flex:1,minWidth:180,background:'#080d14',border:'1px solid #1a2030',borderRadius:7,padding:'7px 10px'}}>
                <span style={{fontSize:11,color:'#5a6a80',whiteSpace:'nowrap'}}>Si j'avais investi</span>
                <input
                  type="number" min="0" placeholder="ex: 100"
                  value={hypotheticalAmount}
                  onChange={e => setHypotheticalAmount(e.target.value)}
                  style={{background:'transparent',border:'none',outline:'none',color:'#c0c8d8',fontSize:12,fontWeight:700,width:70,textAlign:'right'}}
                />
                <span style={{fontSize:11,color:'#5a6a80'}}>$ / trade</span>
              </div>
              {parseFloat(hypotheticalAmount) > 0 && (() => {
                const hypoUsd = parseFloat(hypotheticalAmount);
                const hypoTotal = topWallets.reduce((s, [, ws]) => s + ws.roiList.reduce((a, r) => a + r / 100 * hypoUsd, 0), 0);
                return (
                  <span style={{fontSize:11,fontWeight:700,color:hypoTotal>=0?'#00e5cc':'#ff6b6b',background:'rgba(124,77,255,0.08)',border:'1px solid #7c4dff22',borderRadius:6,padding:'5px 10px',whiteSpace:'nowrap'}}>
                    📊 Simulation : {hypoTotal>=0?'+':''}{hypoTotal.toFixed(2)}$
                  </span>
                );
              })()}
            </div>
            {topWallets.length === 0 ? (
              <div style={{textAlign:'center',padding:'30px 0',color:'#3a4a5a',fontSize:12}}>Aucun wallet copié</div>
            ) : (
              <div style={{overflowY:'auto',maxHeight:260}}>
                {topWallets.map(([wallet, stats], i) => {
                  const cfg    = configs.find(c => c.targetWallet === wallet);
                  const winPct = stats.trades > 0 ? ((stats.wins/stats.trades)*100).toFixed(0) : 0;
                  const roi = stats.invested > 0 ? ((stats.pnl/stats.invested)*100).toFixed(1) : 0;
                  const isExpanded    = expandedWallet === wallet;
                  const hypoUsd      = parseFloat(hypotheticalAmount) || 0;
                  const displayPnl   = hypoUsd > 0 ? stats.roiList.reduce((s,r) => s + r / 100 * hypoUsd, 0) : stats.pnl;
                  const displayBest  = hypoUsd > 0 && stats.roiList.length > 0 ? Math.max(...stats.roiList.map(r => r / 100 * hypoUsd)) : stats.bestPnl;
                  const displayWorst = hypoUsd > 0 && stats.roiList.length > 0 ? Math.min(...stats.roiList.map(r => r / 100 * hypoUsd)) : stats.worstPnl;
                  const walletTrades = isExpanded
                    ? filtered.filter(t => t.walletSuivi === wallet).sort((a,b) => (b.sortieTs||0) - (a.sortieTs||0))
                    : [];
                  return (
                    <div key={wallet} style={{borderBottom:'1px solid #0d1117'}}>
                      <div
                        onClick={() => setExpandedWallet(isExpanded ? null : wallet)}
                        style={{padding:'10px 0',cursor:'pointer',userSelect:'none'}}
                      >
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:5}}>
                          <span style={{fontSize:12,flexShrink:0}}>{i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${i+1}`}</span>
                          <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                            {cfg
                              ? <><span style={{fontSize:11,fontWeight:700,color:'#c0c8d8'}}>{cfg.emoji ? `${cfg.emoji} ` : ''}{cfg.name}</span><span style={{fontSize:10,color:'#3a4a5a',fontFamily:'monospace',marginLeft:6}}>{wallet.slice(0,6)}…{wallet.slice(-4)}</span></>
                              : <span style={{fontSize:11,fontWeight:700,color:'#c0c8d8',fontFamily:'monospace'}}>{wallet.length>16?`${wallet.slice(0,6)}…${wallet.slice(-4)}`:wallet}</span>
                            }
                          </span>
                          <span style={{fontSize:10,color:'#3a4a5a',flexShrink:0,marginRight:4}}>{isExpanded ? '▲' : '▼'}</span>
                          <span style={{fontSize:13,fontWeight:700,color:displayPnl>=0?'#00e5cc':'#ff6b6b',flexShrink:0}}>{fmtUsd(displayPnl)}</span>
                        </div>
                        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                          <span style={{fontSize:10,color:'#5a6a80'}}>{stats.trades} trades</span>
                          <span style={{fontSize:10,fontWeight:600,color:Number(winPct)>=50?'#00e5cc':'#ff6b6b'}}>{winPct}% win</span>
                          <span style={{fontSize:10,color:'#5a6a80'}}>ROI <span style={{color:Number(roi)>=0?'#7c4dff':'#ff6b6b',fontWeight:600}}>{Number(roi)>0?'+':''}{roi}%</span></span>
                          {displayBest > -Infinity && <span style={{fontSize:10,color:'#3a4a5a'}}>↑ <span style={{color:'#00e5cc'}}>{fmtUsd(displayBest)}</span></span>}
                          {displayWorst < Infinity && <span style={{fontSize:10,color:'#3a4a5a'}}>↓ <span style={{color:'#ff6b6b'}}>{fmtUsd(displayWorst)}</span></span>}
                        </div>
                      </div>
                      {isExpanded && (
                        <div style={{paddingBottom:8,borderTop:'1px solid #0d1117'}}>
                          {walletTrades.length === 0
                            ? <div style={{fontSize:10,color:'#3a4a5a',padding:'6px 0'}}>Aucun trade dans cette période</div>
                            : walletTrades.map((t, j) => {
                                const cr = t.closeReason;
                                const closeEmoji = cr==='STOP-LOSS'?'🛑':cr==='TAKE-PROFIT'?'🎯':cr==='TRAILING-STOP'?'📉':cr==='MANUAL'?'✋':cr==='PHANTOM-RECONCILE'?'👻':null;
                                const displayTradePnl = hypoUsd > 0 && t.roi != null ? t.roi / 100 * hypoUsd : t.pnlUsd;
                                return (
                                  <div key={t.id||j} style={{display:'flex',alignItems:'center',gap:6,padding:'5px 0 5px 4px',borderTop:'1px solid #080d14',fontSize:11}}>
                                    <span style={{fontWeight:700,color:'#c0c8d8',width:64,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.symbol||'—'}</span>
                                    <span style={{fontWeight:700,color:(displayTradePnl||0)>=0?'#00e5cc':'#ff6b6b',width:64,textAlign:'right'}}>{displayTradePnl!=null?`${displayTradePnl>=0?'+':''}${displayTradePnl.toFixed(2)}$`:'—'}</span>
                                    <span style={{color:(t.roi||0)>=0?'#7c4dff':'#ff6b6b',width:48,textAlign:'right'}}>{t.roi!=null?`${(t.roi||0)>=0?'+':''}${t.roi.toFixed(1)}%`:'—'}</span>
                                    {closeEmoji && <span title={cr}>{closeEmoji}</span>}
                                    <span style={{marginLeft:'auto',color:'#3a4a5a',fontSize:10}}>{t.sortieTs?timeAgo(t.sortieTs):t.entreeTs?timeAgo(t.entreeTs):''}</span>
                                  </div>
                                );
                              })
                          }
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Historique des trades */}
      {totalTrades > 0 && (
        <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:8}}>
            <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',display:'flex',alignItems:'center',gap:7}}>
              <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><rect x="1"y="1"width="12"height="12"rx="2"stroke="#7c4dff"strokeWidth="1.2"/><path d="M4 7h6M4 4.5h6M4 9.5h4"stroke="#7c4dff"strokeWidth="1.2"strokeLinecap="round"/></svg>
              Historique des Trades Bot
            </div>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              <button onClick={exportCSV} style={{padding:'4px 12px',borderRadius:4,fontSize:10,fontWeight:700,cursor:'pointer',background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc33',color:'#00e5cc',display:'flex',alignItems:'center',gap:5}}>
                <svg viewBox="0 0 12 12"fill="none"width="10"height="10"><path d="M6 1v7M3 5l3 3 3-3M1 10h10"stroke="currentColor"strokeWidth="1.4"strokeLinecap="round"strokeLinejoin="round"/></svg>
                CSV
              </button>
              <div style={{width:1,height:14,background:'#1a2030'}}/>
              {[['date','Date'],['pnl','PNL'],['roi','ROI']].map(([k,l])=>(
                <button key={k} onClick={()=>setSortBy(k)} style={{padding:'4px 10px',borderRadius:4,fontSize:10,fontWeight:600,cursor:'pointer',background:sortBy===k?'rgba(124,77,255,0.15)':'transparent',border:`1px solid ${sortBy===k?'#7c4dff44':'#1a2030'}`,color:sortBy===k?'#7c4dff':'#5a6a80'}}>{l}</button>
              ))}
            </div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{borderBottom:'1px solid #1a2030'}}>
                  {['DATE','TOKEN','WALLET COPIÉ','INVESTI','PNL','ROI','RÉSULTAT'].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#3a4a5a',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((t,i)=>(
                  <tr key={t.id||i} style={{borderBottom:'1px solid #0d1117'}}>
                    <td style={{padding:'10px 12px',fontSize:11,color:'#5a6a80',whiteSpace:'nowrap'}}>
                      {t.entreeTs?new Date(t.entreeTs*1000).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit',year:'2-digit'}):'—'}
                    </td>
                    <td style={{padding:'10px 12px'}}>
                      <div style={{fontSize:12,fontWeight:700,color:'#e0e4ec'}}>{t.symbol||'—'}</div>
                    </td>
                    <td style={{padding:'10px 12px'}}>
                      {t.walletSuivi ? (
                        <div style={{display:'flex',flexDirection:'column',gap:2}}>
                          {(() => { const cfg = configs.find(c => c.targetWallet === t.walletSuivi); return cfg && <span style={{fontSize:11,fontWeight:700,color:'#c0c8d8'}}>{cfg.emoji?`${cfg.emoji} `:''}{cfg.name}</span>; })()}
                          <button
                            onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(t.walletSuivi); setCopiedAddr(t.walletSuivi); setTimeout(()=>setCopiedAddr(null),1500); }}
                            style={{background:'none',border:'none',padding:0,cursor:'pointer',display:'flex',alignItems:'center',gap:4,fontFamily:'monospace',fontSize:10,color:'#7c4dff'}}
                          >
                            {copiedAddr===t.walletSuivi?'✓':`${t.walletSuivi.slice(0,6)}…${t.walletSuivi.slice(-4)}`}
                          </button>
                        </div>
                      ) : '—'}
                    </td>
                    <td style={{padding:'10px 12px',fontSize:12,color:'#c0c8d8'}}>
                      {t.achatUsd>=1000?`$${(t.achatUsd/1000).toFixed(1)}K`:`$${(t.achatUsd||0).toFixed(0)}`}
                    </td>
                    <td style={{padding:'10px 12px',fontSize:12,fontWeight:700,color:(t.pnlUsd||0)>=0?'#00e5cc':'#ff6b6b'}}>
                      {fmtUsd(t.pnlUsd||0)}
                    </td>
                    <td style={{padding:'10px 12px',fontSize:12,fontWeight:700,color:(t.roi||0)>=0?'#00e5cc':'#ff6b6b'}}>
                      {t.roi!=null?`${t.roi>=0?'+':''}${t.roi.toFixed(1)}%`:'—'}
                    </td>
                    <td style={{padding:'10px 12px'}}>
                      <Badge variant={t.resultat==='Gagnant'?'live':'negative'}>
                        {t.resultat||'—'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}